import type { Guide } from '../core/guides';
import { gridStep, type GridSettings } from '../core/snap';
import type { DocumentMeta, Point } from '../core/types';

const GRID_COLOR = 'rgba(140,140,150,0.35)';
const GRID_MAJOR_COLOR = 'rgba(140,140,150,0.6)';
const GUIDE_COLOR = 'rgba(0,180,255,0.9)';
const GUIDE_LOCKED_COLOR = 'rgba(0,180,255,0.45)';
/** Below this on-screen spacing the finest grid lines are skipped as noise. */
const MIN_LINE_SPACING = 4;

export interface FurnitureView {
  ctx: CanvasRenderingContext2D;
  toScreen: (point: Point) => Point;
  zoom: number;
  width: number;
  height: number;
}

/** A crisp one-pixel line needs to sit on a half-pixel centre. */
function crisp(value: number): number {
  return Math.round(value) + 0.5;
}

export function drawGrid(view: FurnitureView, doc: DocumentMeta, grid: GridSettings): void {
  const step = gridStep(grid);
  if (step <= 0) return;
  const { ctx } = view;
  const divisions = Math.max(1, Math.round(grid.subdivisions));
  const drawMinor = step * view.zoom >= MIN_LINE_SPACING;

  ctx.save();
  ctx.lineWidth = 1;
  for (let index = 0; index * step <= doc.width; index += 1) {
    const major = index % divisions === 0;
    if (!major && !drawMinor) continue;
    const x = crisp(view.toScreen({ x: index * step, y: 0 }).x);
    ctx.strokeStyle = major ? GRID_MAJOR_COLOR : GRID_COLOR;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, view.height);
    ctx.stroke();
  }
  for (let index = 0; index * step <= doc.height; index += 1) {
    const major = index % divisions === 0;
    if (!major && !drawMinor) continue;
    const y = crisp(view.toScreen({ x: 0, y: index * step }).y);
    ctx.strokeStyle = major ? GRID_MAJOR_COLOR : GRID_COLOR;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(view.width, y);
    ctx.stroke();
  }
  ctx.restore();
}

export function drawGuides(
  view: FurnitureView,
  guides: readonly Guide[],
  locked: boolean,
): void {
  if (guides.length === 0) return;
  const { ctx } = view;
  ctx.save();
  ctx.lineWidth = 1;
  ctx.strokeStyle = locked ? GUIDE_LOCKED_COLOR : GUIDE_COLOR;
  for (const guide of guides) {
    ctx.beginPath();
    if (guide.axis === 'horizontal') {
      const y = crisp(view.toScreen({ x: 0, y: guide.position }).y);
      ctx.moveTo(0, y);
      ctx.lineTo(view.width, y);
    } else {
      const x = crisp(view.toScreen({ x: guide.position, y: 0 }).x);
      ctx.moveTo(x, 0);
      ctx.lineTo(x, view.height);
    }
    ctx.stroke();
  }
  ctx.restore();
}
